import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { RRIBaseModel } from '../../../common/interfaces/base-model.interface';
import { RRTableAction } from '../../../common/types/table-action.type';
import { RRITableAddActionSettings } from '../../../common/interfaces/table/table-add-action-settings.interface';
import { RRITableEditActionSettings } from '../../../common/interfaces/table/table-edit-action-settings.interface';
import { RRITableDeleteActionSettings } from '../../../common/interfaces/table/table-delete-action-settings.interface';
import { RRIBaseTableActionSettings } from '../../../common/interfaces/table/base-table-action-settings.interface';
import { RRBatchOption } from '../../../common/types/batch-option.type';
import { RR_MODAL_CONTENT_TYPE } from '../../../modal/models/modal-content-type.enum';
import { RRSpinnerModel } from '../../../spinner/models/spinner.model';
import { RR_MODAL_BUTTON_TYPE } from '../../../modal/models/modal-button-type.enum';
import { RR_TABLE_PAGINATION_POSITION } from '../../../common/enum/table/position.enum';
import { RRTableContainerModel } from '../../../common/models/table/table-container.model';
import { RRIPagination } from '../../../common/interfaces/base-pagination.interface';
import { RRISort } from '../../../common/interfaces/base-sort.interface';
import { RRITableActionOutput } from '../../../common/interfaces/table/table-action-output.interface';
import { RRITableRowClick } from '../../../common/interfaces/table/table-row-click.interface';
import { RR_TABLE_ACTION_TYPE } from '../../../common/enum/table/table-action-type.enum';
import { RRIDeleteBatchOptionSettings } from '../../../common/interfaces/table/batch-options.interface';
import { RR_BATCH_TYPE } from '../../../common/enum/table/batch-type.enum';
import { RR_TABLE_ACTION_BEHAVIOUR_TYPE } from '../../../common/enum/table/table-action-behaviour-type.enum';

@Component({
  selector: 'rr-table-container',
  templateUrl: './table-container.component.html',
  styleUrls: ['./table-container.component.scss'],
})
export class TableContainerComponent<T extends RRIBaseModel, R>
  implements OnInit
{
  public readonly POSITION = RR_TABLE_PAGINATION_POSITION;
  public readonly MODAL_CONTENT_TYPE = RR_MODAL_CONTENT_TYPE;
  public showDeleteModal: boolean;
  public deleteModalTitle: string;
  public deleteModalContent: TemplateRef<any>;
  public deleteModalBodyText: string;
  public clickedRow: T;
  public clearBatchOptions: boolean;
  private isBatchOption: boolean;

  @Input() spinnerStatus: RRSpinnerModel;
  @Input() model: RRTableContainerModel<T, R>;
  @Input() selectedRows: T[];
  @Input() selectedRadioEntity: T;
  @Input() addButtonLeft: TemplateRef<any>;
  @Input() addButtonRight: TemplateRef<any>;
  @Input() clearRadioSelection: boolean;

  @Output() paginationChange: EventEmitter<RRIPagination> = new EventEmitter();
  @Output() sortBy: EventEmitter<RRISort<T>> = new EventEmitter();
  @Output() actionClicked: EventEmitter<RRITableActionOutput<T>> =
    new EventEmitter();
  @Output() actionConfirmed: EventEmitter<RRITableActionOutput<T>> =
    new EventEmitter();
  @Output() batchOptionClicked: EventEmitter<RRBatchOption<T, R>> =
    new EventEmitter();
  @Output() batchOptionConfirmed: EventEmitter<RRBatchOption<T, R>> =
    new EventEmitter();
  @Output() rowSelected: EventEmitter<T[]> = new EventEmitter();
  @Output() radioSelected: EventEmitter<T> = new EventEmitter();
  @Output() rowClicked: EventEmitter<RRITableRowClick<T>> = new EventEmitter();
  @Output() changeVisableColumns: EventEmitter<(keyof T)[]> =
    new EventEmitter();
  @Output() radioSelectionCleared: EventEmitter<void> = new EventEmitter();
  @Output() selectAllClicked: EventEmitter<void> = new EventEmitter();

  @ViewChild('confirmDeleteTemplate') confirmDeleteTemplate: TemplateRef<any>;

  constructor(private router: Router, private cdRef: ChangeDetectorRef) {
    this.spinnerStatus = {
      show: false,
      text: 'Loading...',
    };
    this.isBatchOption = false;
    this.showDeleteModal = false;
  }

  ngOnInit(): void {
    if (!this.selectedRows) {
      this.selectedRows = [];
    }
  }

  public get RecordCount(): number {
    return this.model?.tablePagination?.settings?.recordCount ?? 0;
  }

  public onPageChange(event: number): void {
    this.model.tablePagination.settings.currentPage = event;
    this.paginationChange.emit(this.model.tablePagination.settings);
  }

  public onPageSizeChange(event: number): void {
    this.model.tablePagination.settings.currentPage = 1;
    this.model.tablePagination.settings.pageSize = event;
    this.paginationChange.emit(this.model.tablePagination.settings);
  }

  public onSortBy(event: RRISort<T>): void {
    this.model.table.sortBy = event;
    this.sortBy.emit(event);
  }

  public onActionClicked(event: RRITableActionOutput<T>): void {
    const { action, row } = event;

    if (action && action.settings && !action.settings.doNothing) {
      this.clickedRow = row;

      if (action.type === RR_TABLE_ACTION_TYPE.ADD) {
        this.manageAddAction(
          action.settings as RRITableAddActionSettings<T>,
          row,
        );
      }

      if (action.type === RR_TABLE_ACTION_TYPE.EDIT) {
        this.manageEditAction(
          action.settings as RRITableEditActionSettings<T>,
          row,
        );
      }

      if (action.type === RR_TABLE_ACTION_TYPE.DELETE) {
        this.manageDeleteAction(
          action.settings as RRITableDeleteActionSettings<T>,
          row,
        );
      }
    }

    if (this.actionClicked) {
      this.actionClicked.emit(event);
    }
  }

  public onBatchOptionCleared() {
    this.clearBatchOptions = false;
    this.cdRef.detectChanges();
  }

  public onRowSelected(event: T[]): void {
    this.rowSelected.emit(event);
    this.selectedRows = event;
  }
  public onRadioSelected(event: T): void {
    this.radioSelected.emit(event);
    this.clearBatchOptions = false;
    this.selectedRows = [event];
  }
  public onRadioSelectionCleared() {
    this.clearBatchOptions = true;
    this.clearRadioSelection = false;
    this.radioSelectionCleared.emit();
    this.cdRef.detectChanges();
  }

  public onRowClicked(event: RRITableRowClick<T>): void {
    this.rowClicked.emit(event);
  }

  public onVisibleColumnsChange(event: (keyof T)[]): void {
    this.changeVisableColumns.emit(event);
    this.model.table.visibleColumns = event;
  }

  public showBatchOptions(): boolean {
    return this.selectedRows && this.selectedRows.length > 0;
  }

  public onBatchOptionClicked(event: RRBatchOption<T, R>): void {
    if (event.type === RR_BATCH_TYPE.DELETE) {
      const settings = event.settings as RRIDeleteBatchOptionSettings<T>;
      this.deleteModalContent = settings.modalSettings.modalTemplate
        ? settings.modalSettings.modalTemplate
        : this.confirmDeleteTemplate;
      this.deleteModalTitle = settings.modalSettings.modalTitle;
      this.deleteModalBodyText = '';
      this.selectedRows
        .map((row: T) => {
          this.deleteModalBodyText += `${settings.modalSettings.formatEntry(
            row,
          )} <br/>`;
        })
        .toString();
      this.showDeleteModal = true;
      this.isBatchOption = true;
    }

    if (this.batchOptionClicked && !Array.isArray(event.settings)) {
      event.settings.selectedRows = this.selectedRows;
      this.batchOptionClicked.emit(event);
    }
  }

  public onDeleteModalClosed(event: RR_MODAL_BUTTON_TYPE): void {
    if (event === RR_MODAL_BUTTON_TYPE.CONFIRM) {
      if (this.isBatchOption) {
        const option = this.model.visibleBatchOptions.delete;
        option.settings.selectedRows = this.selectedRows;
        this.batchOptionConfirmed.emit(option);
        this.selectedRows = [];
      } else {
        const deleteAction = this.getDeleteAction();
        if (deleteAction && deleteAction.delete) {
          deleteAction.delete(this.clickedRow);
        }
        this.actionConfirmed.emit({
          action: { type: RR_TABLE_ACTION_TYPE.DELETE, settings: deleteAction },
          row: this.clickedRow,
        });
        this.clickedRow = null;
      }

      this.showDeleteModal = false;
      this.isBatchOption = false;
    } else {
      this.showDeleteModal = false;
    }
  }

  public getAddAction(): RRITableAddActionSettings<T> {
    if (
      this.model &&
      this.model.table &&
      this.model.table.tableActions &&
      this.model.table.tableActions.length
    ) {
      const action = this.model.table.tableActions.find(
        (action: RRTableAction<T>) => action.type === RR_TABLE_ACTION_TYPE.ADD,
      );

      return action ? action.settings : null;
    }

    return null;
  }

  public getExcelAction(): RRIBaseTableActionSettings {
    if (
      this.model &&
      this.model.table &&
      this.model.table.tableActions &&
      this.model.table.tableActions.length
    ) {
      const action = this.model.table.tableActions.find(
        (action: RRTableAction<T>) =>
          action.type === RR_TABLE_ACTION_TYPE.EXCEL,
      );

      return action ? action.settings : null;
    }

    return null;
  }

  public onSelectAllClicked(): void {
    this.selectAllClicked.emit();
  }

  private setDeleteModalSettings(row: T) {
    const deleteAction = this.getDeleteAction();
    this.deleteModalContent = deleteAction.modalSettings.modalTemplate
      ? deleteAction.modalSettings.modalTemplate
      : this.confirmDeleteTemplate;
    this.deleteModalTitle = deleteAction.modalSettings.modalTitle;
    this.deleteModalBodyText = deleteAction.modalSettings.formatEntry(row);
    this.showDeleteModal = true;
  }

  private getDeleteAction(): RRITableDeleteActionSettings<T> {
    if (
      this.model &&
      this.model.table &&
      this.model.table.tableActions &&
      this.model.table.tableActions.length
    ) {
      const action = this.model.table.tableActions.find(
        (action: RRTableAction<T>) =>
          action.type === RR_TABLE_ACTION_TYPE.DELETE,
      );

      return action
        ? (action.settings as RRITableDeleteActionSettings<T>)
        : null;
    }

    return null;
  }

  private manageAddAction(
    settings: RRITableAddActionSettings<T>,
    row?: T,
  ): void {
    if (
      settings.behaviourType ===
        RR_TABLE_ACTION_BEHAVIOUR_TYPE.NAVIGATE_TO_URL &&
      settings.formatNavigateToUrl
    ) {
      this.router.navigateByUrl(settings.formatNavigateToUrl(row));
    }
  }

  private manageEditAction(
    settings: RRITableEditActionSettings<T>,
    row?: T,
  ): void {
    if (
      settings.behaviourType ===
        RR_TABLE_ACTION_BEHAVIOUR_TYPE.NAVIGATE_TO_URL &&
      settings.formatNavigateToUrl &&
      row &&
      row.id
    ) {
      this.router.navigateByUrl(settings.formatNavigateToUrl(row));
    }
  }

  private manageDeleteAction(
    settings: RRITableDeleteActionSettings<T>,
    row?: T,
  ): void {
    if (settings.behaviourType === RR_TABLE_ACTION_BEHAVIOUR_TYPE.SHOW_MODAL) {
      this.setDeleteModalSettings(row);
    }
  }
}
